import { useAuth0 } from '@auth0/auth0-react';

import type { IAccessToken, IAccessTokenArgs } from 'types/hooks/useAccessToken';
import { isUnrecoverableAuthError } from 'utils';

const namespace = 'https://dojomojo.com/jwt';

export default function useAccessToken() {
  const { getAccessTokenSilently, logout } = useAuth0();

  const getAccessToken = async ({
    refresh = false,
    brandId
  }: IAccessTokenArgs = {}): Promise<IAccessToken> => {
    const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
    // without an audience Auth0 mints a token our API rejects; the env var is
    // baked in at build time, so no runtime retry or re-login can recover
    if (!audience) throw new Error('VITE_AUTH0_AUDIENCE is not configured');

    try {
      // get current token (retrieves a new token from Auth0 if local cache is empty)
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience,
          ...(brandId ? { brandId } : {})
        },
        cacheMode: refresh ? 'off' : 'on'
      });
      return { accessToken };
    } catch (error) {
      // the session can't be renewed silently; clear local auth state so
      // <App /> sends the user back through an interactive login
      if (isUnrecoverableAuthError(error)) await logout({ openUrl: false });
      throw error;
    }
  };

  return {
    getAccessToken,
    namespace
  };
}
